import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import TextareaAutosize from 'react-autosize-textarea';
import update from 'immutability-helper';
import InvoiceHeader from '../Invoice/InvoiceHeader';
import LineItems from './LineItems';
import StickyRequestBanner from './StickyRequestBanner';
import { postRequest } from '../../requestUtils';
import { analyticsTrack } from '../../analyticsUtils';
import { sum, sumTime } from '../../arrayUtils';
import './RequestEditsInvoice.scss';

class RequestEditsInvoice extends Component {
  constructor(props) {
    super(props);
    const { invoice } = props;
    this.state = {
      feedback: invoice.feedback,
      lineItems: invoice.line_items,
      showModal: false,
      isValid: true,
    };
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.updateFeedback = this.updateFeedback.bind(this);
    this.updateLineItemFeedback = this.updateLineItemFeedback.bind(this);
    this.sendRequestEdit = this.sendRequestEdit.bind(this);
  }

  handleShowModal() {
    const { feedback, lineItems } = this.state;
    if (feedback || Object.values(lineItems).flat().some((li) => li.feedback)) {
      analyticsTrack('Clicked on Send Edits button to open request edits confirmation modal', {
        location: 'Invoices Edit',
      });
      this.setState({ showModal: true });
    } else {
      analyticsTrack('Clicked on Send Edits button to open request edits confirmation modal', {
        location: 'Invoices Edit',
        error: 'Form was invalid',
      });
      this.setState({ isValid: false });
    }
  }

  handleCloseModal() {
    analyticsTrack('Cancelled Send Edits on request edits confirmation modal', {
      location: 'Invoices Edit',
    });
    this.setState({ showModal: false });
  }

  updateFeedback(value) {
    this.setState({ feedback: value, isValid: true });
  }

  updateLineItemFeedback(category, lineItemId, feedback) {
    const { lineItems } = this.state;
    const lineItemIndex = lineItems[category].findIndex((a) => a.id === lineItemId);
    const newLineItems = update(lineItems, { [category]: { [lineItemIndex]: { $merge: { feedback } } } });
    if (Object.values(lineItems).flat().some((li) => li.feedback)) {
      this.setState({ isValid: true });
    }
    this.setState({ lineItems: newLineItems });
  }

  sendRequestEdit() {
    const { invoice, csrfToken } = this.props;
    const { feedback, lineItems } = this.state;
    const lineItemFeedbacks = Object.values(lineItems).flat().filter((li) => li.feedback).map((li) => (
      { id: li.id, feedback: li.feedback }
    ));
    postRequest(
      `/invoices/${invoice.resource_id}/request_edits`,
      { invoice: { feedback, line_items_attributes: lineItemFeedbacks } },
      csrfToken,
    ).then(() => {
      analyticsTrack('Confirmed Send Edits on request edits confirmation modal', {
        location: 'Invoices Edit',
      });
      window.location.href = `/invoices/${invoice.resource_id}`;
    });
  }

  render() {
    const {
      viewerIsEmployer,
      projectName,
      projectResourceId,
      invoice,
      csrfToken,
      location,
      totals,
      workCommitment,
      areaOfExpertises,
      tags,
      jobTitles,
      description,
      responsibilities,
      duration,
      workLocation,
      shortOfficeLocation,
      startDate,
      displayedLaborRate,
      displayedTravelRate,
    } = this.props;
    const {
      feedback, lineItems, showModal, isValid,
    } = this.state;
    const { consulting, travel, expense } = lineItems;

    return (
      <div className="invoice-request-edits">
        <Modal show={showModal} onHide={this.handleCloseModal} size="lg">
          <Modal.Header closeButton className="no-border-bottom" />
          <Modal.Body className="confirm-request-edit p-3 p-md-5">
            <div className="style-guide">
              <h1 className="mb-5">
                You are about to send your comments and request edits on this timesheet.
              </h1>
              <div className="body-text-1 mb-5">
                {invoice.consultant_name}
                {' '}
                will receive your comments and will address them.
                Expect to receive the edited timesheet for approval in the next few days.
              </div>
              <div className="d-flex flex-column flex-md-row gap-3">
                <button
                  className="btn btn-secondary standard-button g-flex-fill me-2"
                  onClick={this.handleCloseModal}
                >
                  No, go back to edits
                </button>
                <button
                  className="btn btn-primary standard-button g-flex-fill"
                  onClick={this.sendRequestEdit}
                >
                  Yes, Send & Request
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
        <StickyRequestBanner
          resourceId={invoice.resource_id}
          isValid={isValid}
          consultantName={invoice.consultant_name}
          handleShowModal={this.handleShowModal}
        />
        <div className="container">
          <InvoiceHeader
            invoiceNumber={invoice.invoice_number}
            id={invoice.resource_id}
            abbreviatedMonth={invoice.abbreviated_month}
            month={invoice.month}
            year={invoice.year}
            status="requesting_edits"
            projectResourceId={projectResourceId}
            projectName={projectName}
            consultantName={invoice.consultant_name}
            memo={invoice.memo}
            totals={totals}
            csrfToken={csrfToken}
            location={location}
            feedback={invoice.feedback}
            editsRequestedBy={invoice.edits_requested_by}
            editsRequestedOn={invoice.edits_requested_on}
            approvedBy={invoice.approved_by}
            approvedOn={invoice.approved_on}
            isProjectTeamMember
            workCommitment={workCommitment}
            areaOfExpertises={areaOfExpertises}
            tags={tags}
            jobTitles={jobTitles}
            description={description}
            responsibilities={responsibilities}
            duration={duration}
            workLocation={workLocation}
            shortOfficeLocation={shortOfficeLocation}
            startDate={startDate}
            buttonStyles="body-text-1"
            showHourWarning={false}
            projectedHours={invoice.projected_hours_by_consultants}
          >
            <div className={`body-text-2 invoice-request-edit ${isValid ? '' : 'not-valid'}`}>
              <div className="demibold mb-5">
                <i className="fa fa-edit" />
                Request general edits here:
              </div>
              <div>
                <TextareaAutosize
                  name="general-request-edit"
                  className="general-request-edit"
                  placeholder="Add general edits requests for the consultant here..."
                  value={feedback}
                  onChange={(e) => this.updateFeedback(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </InvoiceHeader>
          <LineItems
            lineItems={consulting}
            lineItemsGroupName={`Consulting Hours - $${displayedLaborRate}/hr`}
            descriptionLabel="Task Description"
            category="consulting"
            hasDuration
            viewerIsEmployer={viewerIsEmployer}
            rate={displayedLaborRate}
            totalPay={sum(consulting, viewerIsEmployer)}
            totalTime={sumTime(consulting)}
            updateLineItemFeedback={this.updateLineItemFeedback}
            isValid={isValid}
          />
          <LineItems
            lineItems={travel}
            lineItemsGroupName={`Travel Hours - $${displayedTravelRate}/hr`}
            descriptionLabel="Travel Description"
            category="travel"
            hasDuration
            viewerIsEmployer={viewerIsEmployer}
            rate={displayedTravelRate}
            totalPay={sum(travel, viewerIsEmployer)}
            totalTime={sumTime(travel)}
            updateLineItemFeedback={this.updateLineItemFeedback}
            isValid={isValid}
          />

          <LineItems
            lineItems={expense}
            lineItemsGroupName="Expenses"
            descriptionLabel="Expense Description"
            category="expense"
            totalPay={sum(expense, viewerIsEmployer)}
            updateLineItemFeedback={this.updateLineItemFeedback}
            isValid={isValid}
          />
        </div>
      </div>
    );
  }
}

RequestEditsInvoice.propTypes = {
  viewerIsEmployer: PropTypes.bool.isRequired,
  invoice: PropTypes.shape({}).isRequired,
  projectName: PropTypes.string.isRequired,
  projectResourceId: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  totals: PropTypes.shape({}).isRequired,
  workCommitment: PropTypes.number.isRequired,
  areaOfExpertises: PropTypes.arrayOf(PropTypes.string),
  tags: PropTypes.arrayOf(PropTypes.string),
  jobTitles: PropTypes.string,
  description: PropTypes.string.isRequired,
  responsibilities: PropTypes.arrayOf(PropTypes.string).isRequired,
  duration: PropTypes.string.isRequired,
  workLocation: PropTypes.string.isRequired,
  shortOfficeLocation: PropTypes.string.isRequired,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  displayedLaborRate: PropTypes.number.isRequired,
  displayedTravelRate: PropTypes.number.isRequired,
};

RequestEditsInvoice.defaultProps = {
  areaOfExpertises: [],
  tags: [],
  jobTitles: '',
  startDate: null,
};

export default RequestEditsInvoice;
